/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, signal, WritableSignal } from "@angular/core";
import { Router } from "@angular/router";
import {
  AuthChangeEvent,
  Session,
  SupabaseClient,
  User,
} from "@supabase/supabase-js";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Capacitor } from "@capacitor/core";
import { SupabaseClientService } from "./supabase-client";
import { BugsnagService } from "src/app/core/services/bugsnag.service";
import { ModuleLoaderService } from "src/app/shared/services/module-loader.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private supabase: SupabaseClient;
  private currentUser: BehaviorSubject<User | boolean> = new BehaviorSubject<
    User | boolean
  >(false);
  private currentSession: WritableSignal<Session | null> = signal<Session | null>(null);
  readonly currentSessionSignal = this.currentSession.asReadonly();

  constructor(private router: Router, private supabaseClientService: SupabaseClientService, private bugsnagService: BugsnagService, private moduleLoader: ModuleLoaderService) {
    this.supabase = supabaseClientService.supabaseClient;

    this.supabase.auth.onAuthStateChange((event, sess) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        this.currentUser.next(sess!.user);
        this.currentSession.set(sess);
        this.bugsnagService.setUser(
          sess.user?.id,
          sess.user?.email,
          sess.user?.user_metadata?.name
        );
        this.moduleLoader.subscribeToRevenueCatUserData(sess!.user.id);
      } else if (event === "INITIAL_SESSION") {
        if (sess) {
          this.currentUser.next(sess!.user);
          this.currentSession.set(sess);
          localStorage.setItem("isSignedIn", "true");
          this.bugsnagService.setUser(
            sess.user?.id,
            sess.user?.email,
            sess.user?.user_metadata?.name
          );
          this.moduleLoader.subscribeToRevenueCatUserData(sess!.user?.id);
        } else {
          this.currentUser.next(false);
          this.currentSession.set(null);
          localStorage.setItem("isSignedIn", "false");
        }
      } else {
        this.currentUser.next(false);
        this.currentSession.set(null);
        localStorage.setItem("isSignedIn", "false");
      }
    });

    // Trigger initial session load
    this.loadUser();
    this.loadSession();
  }

  async loadUser() {
    if (this.currentUser.value) {
      // User is already set, no need to do anything else
      return;
    }

    const user = await this.supabase.auth.getUser();

    if (user.error && user.error?.status === 403) {
      this.supabase.auth.refreshSession();
    }

    if (user.data.user) {
      this.currentUser.next(user.data.user);
    } else {
      this.currentUser.next(false);
    }
  }

  async loadSession() {
    if (this.currentSession()) {
      // Session is already set, no need to do anything else
      return;
    }
    this.currentSession.set(await (
      await this.supabase.auth.getSession()
    ).data.session);
  }

  async authChanges(
    callback: (event: AuthChangeEvent, session: Session | null) => void
  ) {
    await this.supabase.auth.onAuthStateChange(callback);
  }

  signUp(credentials: {
    forename: any;
    surname: any;
    email: any;
    password: any;
  }) {
    return this.supabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        data: {
          forename: credentials.forename,
          surname: credentials.surname,
        },
      },
    });
  }

  signIn(credentials: { email: any; password: any }) {
    return this.supabase.auth.signInWithPassword(credentials);
  }

  verifyEmail(tokenHash: string) {
    return this.supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: "email"
    })
  }

  async signInWithGoogle() {
    const res = await SocialLogin.login({
      provider: 'google',
      options: {},
    });

    if (res.result.idToken) {
      await this.handleSupabaseSignInWithIdToken("google", res.result.idToken);
    }
  }
  
  async signInWithApple() {
    if (Capacitor.getPlatform() === 'ios') {
      const res = await SocialLogin.login({
        provider: 'apple',
        options: {}
      });
      if (res.result.accessToken) {
        await this.handleSupabaseSignInWithIdToken("apple", res.result.accessToken.token);
      }
    } else {
      const redirectTo = environment.appleRedirectUrl;
      await this.supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo
        }
      });
    }
  }

  async signInWithMicrosoft() {
    const redirectTo = environment.microsoftRedirectUrl;
    await this.supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo,
        scopes: 'email',
      }
    });
  }

  async exchangeCodeForToken(code: string) {
    const { data, error } = await this.supabase.auth.exchangeCodeForSession(code);
    if (error) {
      throw error;
    }
  }

  async handleSupabaseSignInWithIdToken(provider: string, token: string) {
    const { data, error } = await this.supabase.auth.signInWithIdToken({
      provider,
      token,
    });

    if (error) {
      console.error("Error signing in with Id Token:", error);
    } else {
      console.log("Signed in with Id Token:", data);
    }
  }

  async getUserIdentities() {
    const { data } = await this.supabase.auth.getUserIdentities();
    return data.identities;
  }

  async linkIdentityProvider(provider, scopes: string = '', queryParams: any = {}) {
    const redirectTo = environment.googleRedirectUrl;

    await this.loadUser();
    if (this.currentUser.value && typeof this.currentUser.value === 'object' && this.currentUser.value.identities.map(identity => identity.provider).includes(provider)) {
      return this.supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
          scopes,
          queryParams
        },
      });
    }

    return this.supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo,
        scopes,
        queryParams
      },
    });
  }

  async unlinkIdentityProvider(provider) {
    const userIdentities = await this.getUserIdentities();
    const identity = userIdentities.find(
      (identity) => identity.provider === provider
    );

    if (!identity) {
      console.error("Identity not found");
      return;
    }

    const { data, error } = await this.supabase.auth.unlinkIdentity(identity);

    if (error) {
      console.error("Error unlinking identity:", error);
    }

    return data;
  }

  signInWithProvider(provider) {
    const redirectTo = "com.quickdrive.app";
    return this.supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
      },
    });
  }

  sendPwReset(email: any) {
    const redirectTo = environment.passwordResetRedirectUrl;
    return this.supabase.auth.resetPasswordForEmail(email, { redirectTo });
  }

  setPassword(password: string) {
    return this.supabase.auth.updateUser({ password });
  }

  async signOut() {
    await this.supabase.auth.signOut({
      scope: "local",
    });
  }

  async deleteAccount() {
    await this.supabase.rpc("delete_user");
    await this.signOut();
  }

  async setSession(access_token: string, refresh_token: string) {
    return this.supabase.auth.setSession({ access_token, refresh_token });
  }

  getCurrentUser(): Observable<User> {
    return this.currentUser.asObservable() as Observable<User>;
  }

  getCurrentSession(): Session | null {
    return this.currentSession();
  }

  async getUserPlan() {
    const { data, error } = await this.supabase.rpc("get_my_claim", {
      claim: "plan",
    });
    return { data, error };
  }

  getCurrentUserId(): string | null {
    if (this.currentUser.value) {
      return (this.currentUser.value as User)?.id;
    } else {
      return null;
    }
  }

  getCurrentUserEmail(): string | null {
    if (this.currentUser.value) {
      return (this.currentUser.value as User)?.email;
    } else {
      return null;
    }
  }

  signInWithEmail(email: string) {
    return this.supabase.auth.signInWithOtp({ email });
  }

  async updateUserEmail(newEmail: string): Promise<any> {
    const { data, error } = await this.supabase.auth.updateUser({
      email: newEmail
    });
    
    if (error) {
      throw error;
    }
    
    return data;
  }
}
